import { useContext, useMemo, useState } from 'react';

import { UpdateStatusContext } from '@/components/App/NewOrder/entities';
import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import { BccBankStatus } from '@/components/Shared/constants/banks-statuses';
import { Button, InfoBadge, OfferCard } from '@/components/Shared/ui';
import { useAppSelector } from '@/store';

import { AlternativeItem, OfferDetails } from '../../entities';
import { getOfferBodyProducts } from '../../helpers/offerBodyProduct';
import { OfferStepContext } from '../../offer-step';
import type { TBccBankOffer } from '../../types/bcc.types';
import useFormulateOfferItem from './hooks/useFormulateOfferItem';
import useHeaderActions from './hooks/useHeaderActions';

interface IProps {
  bankOffer: TBccBankOffer;
  hasOfferedResponse?: boolean;
}

const BccApprovedItem: React.FC<IProps> = ({
  bankOffer: initialBankOffer,
  hasOfferedResponse
}: IProps) => {
  const { banksOffers, leasingOffers } = useAppSelector(
    (state) => state.offerStep
  );

  const {
    userAccesses: {
      global: { isAdmin }
    }
  } = useAppSelector((state) => state.userInfo);

  const { handleCancelResponse } = useContext(OfferStepContext);
  const { onUpdateOfferStatus } = useContext(UpdateStatusContext);

  const [cancelModal, setCancelModal] = useState(false);

  const {
    additionalProducts,
    bankOffer,
    bccBankContracts,
    getDescription,
    headerIcon,
    headerTitle,
    isBlueColoredStatus,
    isCurrentResponseOffered,
    isMobile,
    offerDetailsModal,
    setOfferDetailsModal
  } = useFormulateOfferItem({
    bankOffer: initialBankOffer,
    hasOfferedResponse
  });

  const { headerActions, showAlternatives } = useHeaderActions({
    bankOffer,
    bccBankContracts,
    setCancelModal,
    setOfferDetailsModal
  });

  const isHasFinancedOffer: boolean = useMemo(
    () =>
      banksOffers.some(
        (offer) => offer.status.code === BccBankStatus.Financed
      ) || leasingOffers.some((offer) => offer.status.code === 'financed'),
    [banksOffers, leasingOffers]
  );

  return (
    <>
      <OfferCard
        // Стили для классов bank-item... расположены в widget BankOffers
        className="bank-item"
        status={isBlueColoredStatus ? 'processing' : 'success'}
        extId={bankOffer.extId || ''}
        isSelected={bankOffer.isOffered && !isBlueColoredStatus}
        statusText={bankOffer.status.name}
        onEditStatus={
          !isHasFinancedOffer && isAdmin
            ? () => onUpdateOfferStatus(bankOffer.id)
            : undefined
        }
      >
        <OfferCard.Header
          title={headerTitle}
          icon={headerIcon}
          headerActions={
            !hasOfferedResponse || isCurrentResponseOffered ? headerActions : []
          }
          info={
            hasOfferedResponse && !isCurrentResponseOffered
              ? 'Есть принятое предложение'
              : undefined
          }
          requestBodyInfo={{
            bankName: bankOffer.bank.name,
            responsePrimaryBody: bankOffer.responsePrimaryBody,
            responseSendedBody: bankOffer.responseSendedBody
          }}
        />
        {bankOffer.status.code === BccBankStatus.AcceptedAlternative && (
          <div className="bank-item__info-badge">
            <InfoBadge title="Данное предложение является альтернативой" />
          </div>
        )}
        <OfferCard.Body
          footerActions={headerActions}
          additionalProducts={additionalProducts}
          products={getOfferBodyProducts({
            bankProduct: bankOffer.product,
            initialPayment: bankOffer.initialPayment,
            isMobile,
            loanAmount: bankOffer.loanAmount,
            monthlyPayment: bankOffer.monthlyPayment,
            rate: bankOffer.rate,
            loanPeriod: bankOffer.loanPeriod
          })}
        />
        {showAlternatives &&
          !isMobile &&
          bankOffer.alternatives?.map((alternativeItem) => (
            <AlternativeItem
              alternativeInfo={alternativeItem}
              bank={bankOffer}
              bccBankContracts={bccBankContracts}
              hasOfferedResponse={!!hasOfferedResponse}
              headerIcon={headerIcon}
              headerTitle={headerTitle}
              isBlueColoredStatus={isBlueColoredStatus}
              isCurrentResponseOffered={!!isCurrentResponseOffered}
              isMobile={isMobile}
              key={alternativeItem.extUuid}
              setOfferDetailsModal={setOfferDetailsModal}
            />
          ))}
      </OfferCard>

      {isMobile &&
        bankOffer.alternatives?.map((alternativeItem) => (
          <AlternativeItem
            alternativeInfo={alternativeItem}
            bank={bankOffer}
            bccBankContracts={bccBankContracts}
            hasOfferedResponse={!!hasOfferedResponse}
            headerIcon={headerIcon}
            headerTitle={headerTitle}
            isBlueColoredStatus={isBlueColoredStatus}
            isCurrentResponseOffered={!!isCurrentResponseOffered}
            isMobile={isMobile}
            key={alternativeItem.extUuid}
            setOfferDetailsModal={setOfferDetailsModal}
          />
        ))}

      <OfferDetails
        bankCode={bankOffer.bank.name}
        bankResponse={{
          allResponse: getDescription()
        }}
        historyData={{
          bank: bankOffer.bank.name.replace(/_/g, ''),
          offerId: bankOffer.id
        }}
        isOpened={!!offerDetailsModal}
        onClose={setOfferDetailsModal}
      />

      <CustomModal
        editClass="cancel-offer-modal"
        isModalOpen={cancelModal}
        handleOk={() => {
          handleCancelResponse(bankOffer);
          setCancelModal(false);
        }}
        handleCancel={() => setCancelModal(false)}
        title="Вы уверены что хотите отклонить предложения данного банка?"
        footer={[
          <Button key="back" onClick={() => setCancelModal(false)}>
            Нет
          </Button>,
          <Button
            key="submit"
            variant="filled-red"
            onClick={() => {
              handleCancelResponse(bankOffer);
              setCancelModal(false);
            }}
          >
            Да, отменить
          </Button>
        ]}
      />
    </>
  );
};

export default BccApprovedItem;
